import React, { useState, useEffect, useCallback,useContext } from 'react';
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { Row, Col, Card, Table, Button } from 'react-bootstrap';
import { API } from '../../api/API';
import { extractError } from '../../api/ErrorUtil';
import { BUSINESS_USERS_URL } from 'config/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { PAGE_SIZE } from 'config/config';
import _ from "lodash";
import {
    containsAlphaNumAndSpaceAndPunctuationOnly
} from 'util/Validators';
import { getRole } from 'util/Lookup';
import { useNavigate } from 'react-router-dom';
import { TenantContext } from '../../tenant/TenantContext';

const BusinessUsersList = () => {

    const filterInitialState = {
        keyword: "",
        role: "",
        errors: {},
        infoMessage: "",
        readOnly: false,
    }

    const filterInitialValidationState = {
        keywordError: false,
        keywordErrMsg: "",
        roleError: false,
        roleErrMsg: "",
    }
    
    const navigate = useNavigate();
    
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(0)
    const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
    const [filterData, setFilterData] = useState(filterInitialState)
    const [filterValidityData, setFilterValidityData] = useState(filterInitialValidationState);
    const { businessConfig } = useContext(TenantContext);

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFilterData({
            ...filterData,
            ...messageReset()
        });
    }

    const fetchUsers = useCallback(async (resetfilter, page) => {
        try {
            let usersResponse = {};
            let params = {
                page: 0,
                size: PAGE_SIZE,
                keyword: "",
                role: ""
            }
            if (!resetfilter) {
                params = {
                    page: page ? currentPage : 0,
                    size: PAGE_SIZE,
                    keyword: filterData.keyword ? filterData.keyword.trim() : "",
                    role: filterData.role ? filterData.role.trim() : ""
                }
            }
            usersResponse = await API.get(BUSINESS_USERS_URL, { params });

            if (resetfilter) {
                setCurrentPage(0);
            }
            setUsers(usersResponse.data.content);
            setTotalNumberOfPages(usersResponse.data.totalPages);

        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setUsers([]);
                setFilterData({ ...filterData, infoMessage: "No users were found" });
            } else {
                setFilterData({ ...filterData, errors: { message, errors, httpStatus } });
            }
        }

    })

    useEffect(() => {
        fetchUsers(false, true);
    }, [currentPage]);

    useEffect(() => {
        if (filterData.keyword && filterData.keyword.length > 3) {
            fetchUsers(false, false);
        }
    }, [filterData.keyword]);

    useEffect(() => {
        if (filterData.role) {
            fetchUsers(false, false);
        }
    }, [filterData.role]);

    const handleFilterKeyDown = async (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            fetchUsers(false, false);
        }
    };
    const clearFilter = async (event) => {
        fetchUsers(true);
    };

    const handlePaging = async (page) => {
        if (page < 0) {
            alert("Reached the beginning of data set")
            page = 0;
        }
        if (page > totalNumberOfPages - 1) {
            alert("Reached the end of data set")
            page = totalNumberOfPages - 1;
        }
        setCurrentPage(page);
    }

    const handleFilterChange = async (event) => {
        if (filterData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        setFilterData({
            ...filterData,
            [name]: value
        })
    }

    const handleFilterValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFilterValidityData({
            ...filterValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })
    }

    const createNewUser = async (event) => {
        event.preventDefault();
        navigate('/businessusers/newuser');
    }

    const editUser = async (event, userDetail) => {
        event.preventDefault();
        navigate('/businessusers/'+userDetail.user.id, { state: { userDetail: userDetail } });

    }

    const deleteUser = async (event, userDetail) => {
        event.preventDefault();
        try {
            await API.delete(BUSINESS_USERS_URL+"/"+userDetail.user.id);
            fetchUsers(true);
            setFilterData({
                ...filterData,
                infoMessage: "User was deleted successfully"
            })

        } catch (exception){
            let errors = extractError(exception);
            setFilterData({
                ...filterData,
                errors: errors
            })
        }
        

    }

    const validateInput = (name, value) => {
        let hasError = false, error = "";
        switch (name) {
            case "keyword":
                if (!value || (value && value.trim()) === "") {
                    hasError = false
                    error = ""
                } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
                    hasError = true
                    error = "Invalid keyword. Only alphanumeric characters are allowed";
                } else {
                    hasError = false
                    error = ""
                }
                break
            default:
                break;
        }

        return { hasError, error };
    }

    return (
        <FormContainer title="User Admin" errors={filterData.errors} infoMessage={filterData.infoMessage} toastCloseFunc={onToastClose}>
            <Row>
                <Col sm={12} md={4} lg={4}>
                    <Button label='New User' onClick={createNewUser}
                        className={'btn-fill pull-right '} size='btn-sm' variant="info">New User</Button>
                </Col>
            </Row>
            <Card>
                <Card.Body>
                    <Row>
                        <Col sm={12} md={4} lg={4}>
                            <FormField name="keyword" label="Keyword" type="text" value={filterData.keyword}
                                placeholder="Email, user name, first name or last name" onChange={handleFilterChange}
                                onBlur={handleFilterValidation} onKeyDown={handleFilterKeyDown}
                                valstate={!filterValidityData.keywordError} valMsg={filterValidityData.keywordErrMsg} />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                            <FormField name="role" label="Role" type="select" value={filterData.role}
                                placeholder="Role" onChange={handleFilterChange} onBlur={handleFilterValidation}
                                valstate={!filterValidityData.roleError} valMsg={filterValidityData.roleErrMsg}>
                                <option value="">Select Role</option>
                                <option value="ROLE_BUSINESS_ADMIN">{getRole("ROLE_BUSINESS_ADMIN")}</option>
                                {(businessConfig.appFeatures.includes('DONATION_MANAGEMENT')) &&
                                <option value="ROLE_DONATION_BOX">{getRole("ROLE_DONATION_BOX")}</option>}
                            </FormField>
                        </Col>
                        <Col sm={6} md={2} lg={2}>
                            <br />
                            <Button variant="info" className="border-0 btn-fill" onClick={clearFilter}><strong><small>Clear Filter</small></strong></Button>
                        </Col>
                        {!_.isEmpty(users) &&
                            <React.Fragment>
                                <Col className='d-flex justify-content-end'>
                                    <Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage - 1) }}>
                                        <h6><FontAwesomeIcon icon={faArrowLeft} />Previous Page</h6>
                                    </Button>
                                    &nbsp;<span className="border-0 text-dark btn"><br /><h6>{currentPage + 1} of {totalNumberOfPages}</h6></span>
                                    &nbsp;<Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage + 1) }}>
                                        <h6>Next Page<FontAwesomeIcon icon={faArrowRight} /></h6>
                                    </Button>
                                </Col>
                            </React.Fragment>}
                    </Row>
                    <Table className="table-hover table-striped">
                        <thead>
                            <tr>
                                <th className="border-0">User Name</th>
                                <th className="border-0">First Name</th>
                                <th className="border-0">Last Name</th>
                                <th className="border-0">Email</th>
                                <th className="border-0">Role</th>
                                <th className="border-0">Edit</th>
                                <th className="border-0">Delete</th>
                            </tr>

                        </thead>
                        <tbody>
                            {users && !_.isEmpty(users) && users.map((userDetail, idx) => (
                                <tr key={idx}>
                                    <td>{userDetail.user.userName}</td>
                                    <td>{userDetail.user.firstName}</td>
                                    <td>{userDetail.user.lastName}</td>
                                    <td>{userDetail.user.email}</td>
                                    <td>{getRole(userDetail.userRole.role)}</td>
                                    <td>
                                        <Button variant="dark" className="border-0 text-dark" onClick={evt => editUser(evt, userDetail)}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                    </td>
                                    <td>
                                        <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteUser(evt, userDetail)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </FormContainer >
    )
}

export default BusinessUsersList